import { useAuthenticator } from "@aws-amplify/ui-react";
import { fetchAuthSession } from "aws-amplify/auth";
import { Download, Trash, Trash2, UserPlus } from "lucide-react";
import { type FC, useCallback, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import * as characterApi from "../api/character";
import * as chatRoomApi from "../api/chat-room";
import * as llmApi from "../api/llm";
import * as messageApi from "../api/message";
import * as mobApi from "../api/mob";
import { AddParticipantModal } from "../components/add-participant-modal";
import { ConfirmDialog } from "../components/confirm-dialog";
import { EditProfileModal } from "../components/edit-profile-modal";
import { EditSceneModal } from "../components/edit-scene-modal";
import { Header } from "../components/header";
import { ImageUploadModal } from "../components/image-upload-modal";
import { ParticipantControl } from "../components/participant-control";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import type * as types from "../lib/types";

function ChatHeader({
  groupName,
  phase,
  messageCount,
  onExport,
  onDeleteLastMessage,
  onDeleteAllMessages,
}: {
  groupName: string;
  phase: number;
  messageCount: number;
  onExport: () => void;
  onDeleteLastMessage: () => void;
  onDeleteAllMessages: () => void;
}) {
  return (
    <div className="flex justify-between items-center p-4 border-b">
      <div className="flex space-x-2">
        <Button variant="outline" onClick={onDeleteLastMessage}>
          <Trash className="h-4 w-4 mr-2" />
          一つ戻る
        </Button>
        <Button
          variant="outline"
          onClick={onDeleteAllMessages}
          className="text-red-600 border-red-600 hover:bg-red-100"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          全て削除
        </Button>
      </div>
      <div className="text-center">
        <h1 className="text-xl font-bold flex items-center">
          {groupName}
          <span className="ml-2 text-sm font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
            フェーズ {phase}
          </span>
        </h1>
        <div className="flex items-center justify-center space-x-2 mt-1">
          <span className="text-sm font-semibold bg-green-100 text-green-800 px-3 py-1 rounded-full">
            {messageCount} メッセージ
          </span>
        </div>
      </div>
      <Button variant="outline" onClick={onExport}>
        <Download className="h-4 w-4 mr-2" />
        保存
      </Button>
    </div>
  );
}

function MessageList({ messages }: { messages: types.Message[] }) {
  return (
    <ScrollArea className="flex-grow p-4">
      {messages.map((message) => (
        <div key={message.id} className="mb-4">
          <div className="font-bold">{message.sender}</div>
          {message.type === "image" && message.image_url ? (
            <div className="bg-gray-100 rounded-lg p-2 mt-1">
              <img
                src={message.image_url}
                alt="Uploaded"
                className="max-w-full h-auto rounded"
                style={{ maxHeight: "400px" }}
              />
            </div>
          ) : (
            <div className="bg-gray-100 rounded-lg p-2 mt-1">{message.content}</div>
          )}
          <div className="text-xs text-gray-500 mt-1">{message.timestamp}</div>
        </div>
      ))}
    </ScrollArea>
  );
}

export const Chat: FC = () => {
  const params = useParams();
  const mainCharacterId = params.characterId as string;
  const chatRoomId = params.chatRoomId as string;

  const [messages, setMessages] = useState<types.Message[]>([]);
  const [characters, setCharacters] = useState<types.CharacterInChatRoom[]>([]);
  const [mobs, setMobs] = useState<types.Mob[]>([]);
  const [activeParticipant, setActiveParticipant] = useState<string | undefined>(undefined);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [models, setModels] = useState<llmApi.LLMModel[]>([]);
  const [roomDetails, setRoomDetails] = useState<types.ChatRoomDetails | undefined>(undefined);
  const [token, setToken] = useState<string | undefined>(undefined);
  const [accountId, setAccountId] = useState<string | undefined>(undefined);
  const [isGenerating, setIsGenerating] = useState(false);

  const [isDeleteLastMessageDialogOpen, setIsDeleteLastMessageDialogOpen] = useState(false);
  const [isDeleteAllMessagesDialogOpen, setIsDeleteAllMessagesDialogOpen] = useState(false);

  const MAX_PARTICIPANTS = 3;
  const MESSAGE_CANDIDATES_COUNT = 5;

  const [availableCharacters, setAvailableCharacters] = useState<types.Character[]>([]);

  // Image upload states
  const [isImageUploadModalOpen, setIsImageUploadModalOpen] = useState(false);
  const [currentUploadingParticipant, setCurrentUploadingParticipant] = useState<string | null>(null);
  const [existingImages, setExistingImages] = useState<messageApi.ImageInfo[]>([]);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const init = async () => {
      try {
        const authSession = await fetchAuthSession();
        const maybeToken = authSession.tokens?.idToken?.toString();
        if (!maybeToken) {
          toast.error("認証情報が見つかりません\nリロードして再度ログインしてください");
          return;
        }
        setToken(maybeToken);

        const maybeAccountId = user?.userId;
        if (!maybeAccountId) {
          toast.error("アカウント情報が見つかりません\nリロードして再度ログインしてください");
          return;
        }
        setAccountId(maybeAccountId);

        const { llm_models } = await llmApi.listLlmModels(maybeToken);
        setModels(llm_models);

        const { characters: c } = await characterApi.getCharactersByChatRoomId(maybeToken, chatRoomId);
        setCharacters(
          c.map((_) => ({
            ..._,
            llm_model: llm_models.find((model) => model.id === _.llm_model_id) ?? llm_models[0],
            message: "",
            userMessage: "",
            profile: _.profile ? { description: _.profile } : undefined,
            scene: _.scene ? { description: _.scene } : undefined,
          })),
        );

        const { characters: allCharacters } = await characterApi.listCharacters(maybeToken);
        setAvailableCharacters(allCharacters.filter((_) => !c.some((c) => c.id === _.id)));

        const { mobs: m } = await mobApi.getMobsByChatRoomId(maybeToken, chatRoomId);
        setMobs(
          m.map((_) => ({
            ..._,
            profile: { description: _.profile ?? "" },
            llm_model: llm_models.find((model) => model.id === _.llm_model_id) ?? llm_models[0],
            message: "",
            userMessage: "",
          })),
        );

        const sentMessages = await fetchSentMessages(maybeToken, chatRoomId, c, m);

        const chat_room = await chatRoomApi.getChatRoom(maybeToken, chatRoomId);
        setRoomDetails({
          id: chat_room.id,
          name: chat_room.name,
          phase: chat_room.phase,
          characters: characters,
          mobs: mobs,
          messageCount: sentMessages.length,
          mainCharacterId: chat_room.character_id,
        });

        toast.success("データ管理タグに参加しました");
      } catch (error) {
        console.error(error);
        toast.error("データ管理タグへの参加に失敗しました");
      }
    };

    init();
  }, [chatRoomId]);

  useEffect(() => {
    const sorted = characters.sort((a) => (a.id === mainCharacterId ? -1 : 1));
    setCharacters(sorted);
  }, [characters, mainCharacterId]);

  const fetchSentMessages = async (
    token: string,
    chatRoomId: string,
    characters: { id: string; name: string }[],
    mobs: { id: string; name: string }[],
  ) => {
    try {
      const { messages: allMessages } = await messageApi.listMessages(token, chatRoomId);
      const sentMessages = allMessages.filter((_) => _.status === "sent");
      setMessages(
        sentMessages.map((_) => ({
          ..._,
          sender: _.character_id
            ? // biome-ignore lint/style/noNonNullAssertion: <explanation>
              characters.find((c) => c.id === _.character_id)!.name
            : // biome-ignore lint/style/noNonNullAssertion: <explanation>
              mobs.find((m) => m.id === _.mob_id)!.name,
          timestamp: _.created_at,
          type: _.type || "text",
        })),
      );
      return sentMessages;
    } catch (error) {
      console.error(error);
      toast.error("メッセージの取得に失敗しました");
      return [];
    }
  };

  const { user } = useAuthenticator((context) => [context.user]);

  const handleJoinCharacter = useCallback(
    async (
      character_id: string,
      character_name: string,
      character_unique_name: string,
      characterInput: characterApi.JoinCharacterChatRoomInput,
    ) => {
      if (characters.length + mobs.length >= MAX_PARTICIPANTS) {
        console.warn("Maximum number of participants reached");
        return;
      }
      if (characters.some((c) => c.id === character_id)) {
        console.warn("Character already exists in the chat room");
        return;
      }
      try {
        if (!token) throw new Error("token is undefined");
        await characterApi.joinCharacterChatRoom(token, chatRoomId, character_id, {
          llm_model_id: characterInput.llm_model_id,
          profile: characterInput.profile,
          scene: characterInput.scene,
        });
        setCharacters((prev) => {
          const character: types.CharacterInChatRoom = {
            id: character_id,
            name: character_name,
            unique_name: character_unique_name,
            llm_model: models.find((model) => model.id === characterInput.llm_model_id) ?? models[0],
            message: "",
            userMessage: "",
            profile: characterInput.profile ? { description: characterInput.profile } : undefined,
            scene: characterInput.scene ? { description: characterInput.scene } : undefined,
          };
          const newCharacters = [...prev, character];
          setRoomDetails((prevDetails) => ({
            // biome-ignore lint/style/noNonNullAssertion: <explanation>
            ...prevDetails!,
            characters: newCharacters,
          }));
          return newCharacters;
        });
        toast.success("キャラクターを追加しました");
      } catch (error) {
        console.error(error);
        toast.error("キャラクターの追加に失敗しました");
      }
    },
    [characters, chatRoomId, mobs.length, models, token],
  );

  const handleAddMob = useCallback(
    async (mobInput: mobApi.RegisterMobInput) => {
      if (characters.length + mobs.length >= MAX_PARTICIPANTS) {
        console.warn("Maximum number of participants reached");
        return;
      }
      try {
        if (!token) throw new Error("token is undefined");
        const newMob = await mobApi.registerMob(token, {
          chat_room_id: chatRoomId,
          name: mobInput.name,
          profile: mobInput.profile,
          llm_model_id: mobInput.llm_model_id || models[0].id,
        });
        setMobs((prev) => {
          const mob: types.Mob = {
            ...mobInput,
            id: newMob.id,
            llm_model: models.find((model) => model.id === mobInput.llm_model_id) ?? models[0],
            message: "",
            userMessage: "",
            profile: { description: mobInput.profile },
          };
          const newMobs = [...prev, mob];
          setRoomDetails((prevDetails) => ({
            // biome-ignore lint/style/noNonNullAssertion: <explanation>
            ...prevDetails!,
            mobs: newMobs,
          }));
          return newMobs;
        });
        toast.success("モブを追加しました");
      } catch (error) {
        console.error(error);
        toast.error("モブの追加に失敗しました");
      }
    },
    [characters.length, mobs.length, token, chatRoomId, models],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const handleSendMessage = useCallback(
    async (participant: types.CharacterInChatRoom | types.Mob, message: string, turnId?: string) => {
      try {
        if (!token || !accountId) throw new Error("認証情報が見つかりません");
        await messageApi.sendMessage(token, accountId, chatRoomId, {
          turn_id: turnId ?? uuidv4(),
          character_id: characters.find((_) => _.id === participant.id)?.id ?? undefined,
          mob_id: mobs.find((_) => _.id === participant.id)?.id ?? undefined,
          content: message,
          generated_by: accountId,
          llm_model_id: null,
          feedback: "chosen",
          status: "sent",
        });

        await fetchSentMessages(token, chatRoomId, characters, mobs);

        setCharacters((prev) => prev.map((c) => ({ ...c, message: "", userMessage: "" })));
        setMobs((prev) => prev.map((m) => ({ ...m, message: "", userMessage: "" })));
        setActiveParticipant(undefined);
        toast.success("メッセージを送信しました");
      } catch (error) {
        console.error(error);
        toast.error("メッセージの送信に失敗しました");
      }
    },
    [accountId, characters, chatRoomId, mobs, token],
  );

  const handleCharacterNameChange = useCallback(
    async (character: types.Character, name: string) => {
      try {
        if (!token) throw new Error("認証情報が見つかりません");
        await characterApi.updateCharacter(token, character.id, {
          name: name,
        });
        setCharacters((prev) => prev.map((c) => (c.id === character.id ? { ...c, name } : c)));
        toast.success("キャラクター名を変更しました");
      } catch (error) {
        console.error(error);
        toast.error("キャラクター名の変更に失敗しました");
      }
    },
    [token],
  );

  const handleMobNameChange = useCallback(
    async (mob: types.Mob, name: string) => {
      try {
        if (!token) throw new Error("認証情報が見つかりません");
        await mobApi.updateMob(token, mob.id, {
          name: name,
          profile: mob.profile?.description,
          llm_model_id: mob.llm_model.id,
        });
        toast.success("モブ名を変更しました");
      } catch (error) {
        console.error(error);
        toast.error("モブ名の変更に失敗しました");
      }
    },
    [token],
  );

  const handleCharacterGenerateMessage = useCallback(
    async (characterInChatRoom: types.CharacterInChatRoom) => {
      try {
        if (!token || !accountId) throw new Error("認証情報が見つかりません");
        setIsGenerating(true);
        const turnId = uuidv4();
        const candidate = await characterApi.generateCharacterMessages(
          token,
          characterInChatRoom.id,
          chatRoomId,
          MESSAGE_CANDIDATES_COUNT,
        );

        const messages = await Promise.all(
          candidate.messages.map(async (message) => {
            const { id } = await messageApi.sendMessage(token, accountId, chatRoomId, {
              turn_id: turnId,
              character_id: characterInChatRoom.id,
              content: message,
              generated_by: accountId,
              llm_model_id: characterInChatRoom.llm_model.id,
            });
            return { id, content: message } as const;
          }),
        );

        return { turnId, candidates: messages } as const;
      } catch (error) {
        console.error(error);
        toast.error("メッセージの生成に失敗しました");
      } finally {
        setIsGenerating(false);
      }
    },
    [token, chatRoomId, accountId],
  );

  const handleMobGenerateMessage = useCallback(
    async (mob: types.Mob) => {
      try {
        if (!token || !accountId) throw new Error("認証情報が見つかりません");
        setIsGenerating(true);
        const turnId = uuidv4();
        const candidates = await mobApi.generateMobMessages(token, mob.id, chatRoomId, MESSAGE_CANDIDATES_COUNT);

        const messages = await Promise.all(
          candidates.messages.map(async (message) => {
            const { id } = await messageApi.sendMessage(token, accountId, chatRoomId, {
              turn_id: turnId,
              mob_id: mob.id,
              content: message,
              generated_by: accountId,
              llm_model_id: mob.llm_model.id,
            });
            return { id, content: message } as const;
          }),
        );

        return { turnId, candidates: messages } as const;
      } catch (error) {
        console.error(error);
        toast.error("メッセージの生成に失敗しました");
      } finally {
        setIsGenerating(false);
      }
    },
    [token, chatRoomId, accountId],
  );

  const handleFeedbackMessage = useCallback(
    async (messageId: string, feedback: types.Feedback) => {
      try {
        if (!token) throw new Error("認証情報が見つかりません");
        setMessages((prev) => prev.map((msg) => (msg.id === messageId ? { ...msg, feedback } : msg)));
        await messageApi.updateFeedback(token, messageId, feedback);
        toast.success("メッセージのフィードバックを更新しました");
      } catch (error) {
        console.error(error);
        toast.error("メッセージのフィードバックに失敗しました");
      }
    },
    [token],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const handleUpdateMessageStatus = useCallback(
    async (messageId: string, status: types.Status) => {
      try {
        if (!token) throw new Error("認証情報が見つかりません");
        await messageApi.updateStatus(token, messageId, status);
        await fetchSentMessages(token, chatRoomId, characters, mobs);
        toast.success("メッセージのステータスを更新しました");
      } catch (error) {
        console.error(error);
        toast.error("メッセージのステータスの更新に失敗しました");
      }
    },
    [token, chatRoomId, characters, mobs],
  );

  const [editingParticipant, setEditingParticipant] = useState<types.CharacterInChatRoom | types.Mob | null>(null);
  const [editingSceneProfile, setEditingSceneProfile] = useState<"scene" | "profile" | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const handleEditProfile = useCallback((participant: types.CharacterInChatRoom | types.Mob) => {
    setEditingParticipant(participant);
    setEditingSceneProfile("profile");
    setIsEditModalOpen(true);
  }, []);

  const handleEditScene = useCallback(
    (participant: types.CharacterInChatRoom) => {
      if (participant.id === mainCharacterId) {
        setEditingParticipant(participant);
        setEditingSceneProfile("scene");
        setIsEditModalOpen(true);
      }
    },
    [mainCharacterId],
  );

  const handleSaveParticipantEdit = useCallback(
    async (data: { profile?: string; scene?: string }) => {
      if (editingParticipant) {
        try {
          if (!token) throw new Error("認証情報が見つかりません");
          const character = characters.find((_) => _.id === editingParticipant.id);
          const mob = mobs.find((_) => _.id === editingParticipant.id);
          if (character) {
            const profile = data.profile ? { description: data.profile } : character.profile;
            const scene = data.scene ? { description: data.scene } : character.scene;
            const updatedParticipant = { ...editingParticipant, profile: profile, scene: scene };
            await characterApi.updateCharacterInChatRoom(token, chatRoomId, character.id, {
              llm_model_id: character.llm_model.id,
              profile: profile?.description ?? null,
              scene: scene?.description ?? null,
            });
            setCharacters((prev) =>
              prev.map((c) => (c.id === character.id ? (updatedParticipant as types.CharacterInChatRoom) : c)),
            );
          } else if (mob) {
            const profile = data.profile ? { description: data.profile } : mob.profile;
            await mobApi.updateMob(token, mob.id, {
              name: mob.name,
              llm_model_id: mob.llm_model.id,
              profile: profile?.description ?? undefined,
            });
            const updatedParticipant = { ...editingParticipant, profile: profile };
            setMobs((prev) => prev.map((m) => (m.id === mob.id ? (updatedParticipant as types.Mob) : m)));
          }
          setEditingParticipant(null);
          setIsEditModalOpen(false);
          toast.success("キャラクターまたはモブの編集に成功しました");
        } catch (error) {
          console.error(error);
          toast.error("キャラクターまたはモブの編集に失敗しました");
        }
      }
    },
    [characters, chatRoomId, editingParticipant, mobs, token],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const handleDeleteLastMessage = useCallback(async () => {
    try {
      if (!token) throw new Error("認証情報が見つかりません");
      setMessages((prev) => prev.slice(0, -1));
      await chatRoomApi.rollbackMessage(token, chatRoomId);
      await fetchSentMessages(token, chatRoomId, characters, mobs);
      toast.success("直近のメッセージを削除しました");
    } catch (error) {
      console.error(error);
      toast.error("直近のメッセージの削除に失敗しました");
    }
  }, [characters, chatRoomId, mobs, token]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const handleDeleteAllMessages = useCallback(async () => {
    try {
      if (!token) throw new Error("認証情報が見つかりません");
      await chatRoomApi.clearMessage(token, chatRoomId);
      await fetchSentMessages(token, chatRoomId, characters, mobs);
      toast.success("すべてのメッセージを削除しました");
    } catch (error) {
      console.error(error);
      toast.error("メッセージの削除に失敗しました");
    }
  }, [characters, chatRoomId, mobs, token]);

  const handleExport = useCallback(async () => {
    try {
      if (!token) throw new Error("認証情報が見つかりません");
      await chatRoomApi.exportChatRoom(token, chatRoomId);
      toast.success("データ管理タグをエクスポートしました");
    } catch (error) {
      console.error(error);
      toast.error("データ管理タグのエクスポートに失敗しました");
    }
  }, [token, chatRoomId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const handleEditMessage = useCallback(
    async (messageId: string, content: string) => {
      try {
        if (!token) throw new Error("認証情報が見つかりません");
        await messageApi.updateContent(token, messageId, { content });
        await fetchSentMessages(token, chatRoomId, characters, mobs);
        toast.success("メッセージを更新しました");
      } catch (error) {
        console.error(error);
        toast.error("メッセージの更新に失敗しました");
      }
    },
    [token, chatRoomId, characters, mobs],
  );

  const handleUploadImage = useCallback(
    async (participantId: string) => {
      setCurrentUploadingParticipant(participantId);

      try {
        if (!token) throw new Error("認証情報が見つかりません");
        const { images } = await messageApi.listImages(token);
        setExistingImages(images);
      } catch (error) {
        console.error("Failed to load existing images:", error);
      }

      setIsImageUploadModalOpen(true);
    },
    [token],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const handleUploadNewImage = useCallback(
    async (file: File) => {
      if (!token || !accountId || !currentUploadingParticipant) {
        toast.error("認証情報が見つかりません");
        return;
      }

      setIsUploadingImage(true);

      try {
        const reader = new FileReader();
        const base64Promise = new Promise<string>((resolve, reject) => {
          reader.onload = () => {
            const base64 = reader.result?.toString().split(",")[1];
            if (base64) {
              resolve(base64);
            } else {
              reject(new Error("Failed to convert file to base64"));
            }
          };
          reader.onerror = reject;
        });
        reader.readAsDataURL(file);

        const base64Data = await base64Promise;
        const fileExtension = file.name.split(".").pop()?.toLowerCase() || "jpg";

        const character = characters.find((c) => c.id === currentUploadingParticipant);
        const mob = mobs.find((m) => m.id === currentUploadingParticipant);

        await messageApi.uploadImage(token, accountId, chatRoomId, {
          turn_id: uuidv4(),
          character_id: character?.id,
          mob_id: mob?.id,
          image_data: base64Data,
          file_extension: fileExtension,
          generated_by: accountId,
        });

        await fetchSentMessages(token, chatRoomId, characters, mobs);

        toast.success("画像をアップロードしました");
      } catch (error) {
        console.error("Failed to upload image:", error);
        toast.error("画像のアップロードに失敗しました");
      } finally {
        setIsUploadingImage(false);
      }
    },
    [token, accountId, currentUploadingParticipant, chatRoomId, characters, mobs],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const handleSelectExistingImage = useCallback(
    async (messageId: string) => {
      if (!token || !accountId || !currentUploadingParticipant) {
        toast.error("認証情報が見つかりません");
        return;
      }

      try {
        const character = characters.find((c) => c.id === currentUploadingParticipant);
        const mob = mobs.find((m) => m.id === currentUploadingParticipant);

        await messageApi.sendExistingImage(token, accountId, chatRoomId, {
          turn_id: uuidv4(),
          character_id: character?.id,
          mob_id: mob?.id,
          source_message_id: messageId,
          generated_by: accountId,
        });

        await fetchSentMessages(token, chatRoomId, characters, mobs);

        toast.success("画像を送信しました");
      } catch (error) {
        console.error("Failed to send existing image:", error);
        toast.error("画像の送信に失敗しました");
      }
    },
    [token, accountId, currentUploadingParticipant, chatRoomId, characters, mobs],
  );

  return (
    <div className="h-screen flex flex-col bg-white">
      <Header />
      <ChatHeader
        groupName={roomDetails?.name ?? ""}
        phase={roomDetails?.phase ?? 0}
        messageCount={messages.length}
        onExport={handleExport}
        onDeleteLastMessage={() => setIsDeleteLastMessageDialogOpen(true)}
        onDeleteAllMessages={() => setIsDeleteAllMessagesDialogOpen(true)}
      />
      <MessageList messages={messages} />
      <div className="border-t p-4">
        <div className="flex justify-between mb-4">
          <h2 className="text-lg font-semibold">参加者</h2>
          <Button
            onClick={() => setIsAddModalOpen(true)}
            disabled={characters.length + mobs.length >= MAX_PARTICIPANTS}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            参加者を追加 ({characters.length + mobs.length}/{MAX_PARTICIPANTS})
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-4">
          {characters.map((character) => (
            <ParticipantControl
              key={character.id}
              participant={character}
              type="character"
              isMainCharacter={character.id === mainCharacterId}
              isGenerating={isGenerating}
              onSend={(message, turnId) => handleSendMessage(character, message, turnId)}
              onNameChange={(name) =>
                setCharacters((prev) => {
                  const newCharacters = prev.map((c) => (c.id === character.id ? { ...c, name } : c));
                  // biome-ignore lint/style/noNonNullAssertion: <explanation>
                  setRoomDetails((prevDetails) => ({ ...prevDetails!, characters: newCharacters }));
                  return newCharacters;
                })
              }
              onNameChangeFinished={async (name) => {
                await handleCharacterNameChange(character, name);
              }}
              generateMessageHandler={async () => {
                const generated = await handleCharacterGenerateMessage(character);
                if (!generated) {
                  toast.error("メッセージの生成に失敗しました");
                  return;
                }
                return generated;
              }}
              onUserMessageChange={(message) =>
                setCharacters((prev) => {
                  const newCharacters = prev.map((c) => (c.id === character.id ? { ...c, userMessage: message } : c));
                  // biome-ignore lint/style/noNonNullAssertion: <explanation>
                  setRoomDetails((prevDetails) => ({ ...prevDetails!, characters: newCharacters }));
                  return newCharacters;
                })
              }
              onUserMessageSend={async () => {
                if (character.userMessage) {
                  await handleSendMessage(character, character.userMessage);
                  setCharacters((prev) =>
                    prev.map((c) =>
                      c.id === character.id
                        ? {
                            ...c,
                            userMessage: "",
                          }
                        : c,
                    ),
                  );
                }
              }}
              onModelChange={async (model) => {
                if (!token) throw new Error("認証情報が見つかりません");
                const newCharacter = {
                  ...character,
                  llm_model: model,
                  llm_model_id: model.id,
                };
                await characterApi.updateCharacterInChatRoom(token, chatRoomId, character.id, {
                  llm_model_id: model.id,
                  profile: newCharacter.profile?.description ?? null,
                  scene: newCharacter.scene?.description ?? null,
                });
                setCharacters((prev) => {
                  const newCharacters = prev.map((c) => (c.id === character.id ? newCharacter : c));
                  // biome-ignore lint/style/noNonNullAssertion: <explanation>
                  setRoomDetails((prevDetails) => ({ ...prevDetails!, characters: newCharacters }));
                  return newCharacters;
                });
              }}
              onEditProfile={() => handleEditProfile(character)}
              onEditScene={() => handleEditScene(character)}
              onFeedbackMessage={(messageId, feedback) => handleFeedbackMessage(messageId, feedback)}
              onUpdateStatus={(messageId, status) => handleUpdateMessageStatus(messageId, status)}
              onEditMessage={handleEditMessage}
              onUploadImage={() => handleUploadImage(character.id)}
              disabled={activeParticipant !== undefined && activeParticipant !== character.id}
              models={models}
            />
          ))}
          {mobs.map((mob) => (
            <ParticipantControl
              key={mob.id}
              participant={mob}
              type="mob"
              isMainCharacter={false}
              isGenerating={isGenerating}
              onSend={(message) => handleSendMessage(mob, message)}
              onNameChange={(name) =>
                setMobs((prev) => {
                  const newMobs = prev.map((m) => (m.id === mob.id ? { ...m, name } : m));
                  // biome-ignore lint/style/noNonNullAssertion: <explanation>
                  setRoomDetails((prevDetails) => ({ ...prevDetails!, mobs: newMobs }));
                  return newMobs;
                })
              }
              onNameChangeFinished={async (name) => {
                await handleMobNameChange(mob, name);
              }}
              generateMessageHandler={async () => {
                const generated = await handleMobGenerateMessage(mob);
                if (!generated) {
                  toast.error("メッセージの生成に失敗しました");
                  return;
                }
                return generated;
              }}
              onUserMessageChange={(message) =>
                setMobs((prev) => {
                  const newMobs = prev.map((m) => (m.id === mob.id ? { ...m, userMessage: message } : m));
                  // biome-ignore lint/style/noNonNullAssertion: <explanation>
                  setRoomDetails((prevDetails) => ({ ...prevDetails!, mobs: newMobs }));
                  return newMobs;
                })
              }
              onUserMessageSend={async () => {
                if (mob.userMessage) {
                  await handleSendMessage(mob, mob.userMessage);
                  setMobs((prev) => prev.map((m) => (m.id === mob.id ? { ...m, userMessage: "" } : m)));
                }
              }}
              onModelChange={async (model) => {
                if (!token) throw new Error("認証情報が見つかりません");
                const newMob = {
                  ...mob,
                  llm_model: model,
                  llm_model_id: model.id,
                };
                await mobApi.updateMob(token, mob.id, {
                  name: mob.name,
                  profile: mob.profile?.description,
                  llm_model_id: model.id,
                });
                setMobs((prev) => {
                  const newMobs = prev.map((m) => (m.id === mob.id ? newMob : m));
                  // biome-ignore lint/style/noNonNullAssertion: <explanation>
                  setRoomDetails((prevDetails) => ({ ...prevDetails!, mobs: newMobs }));
                  return newMobs;
                });
              }}
              onEditProfile={() => handleEditProfile(mob)}
              onEditScene={() => {}}
              onFeedbackMessage={(messageId, feedback) => handleFeedbackMessage(messageId, feedback)}
              onUpdateStatus={(messageId, status) => handleUpdateMessageStatus(messageId, status)}
              onEditMessage={handleEditMessage}
              onUploadImage={() => handleUploadImage(mob.id)}
              disabled={activeParticipant !== undefined && activeParticipant !== mob.id}
              models={models}
            />
          ))}
        </div>
      </div>
      <AddParticipantModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onJoinCharacter={async (characterId, characterName, characterUniqueName, characterInput) => {
          await handleJoinCharacter(characterId, characterName, characterUniqueName, characterInput);
        }}
        onAddMob={handleAddMob}
        availableCharacters={availableCharacters}
        remainingSlots={MAX_PARTICIPANTS - (characters.length + mobs.length)}
        chatRoomId={chatRoomId}
        defaultLlmModel={models[0]}
      />
      {editingParticipant && editingSceneProfile === "profile" && (
        <EditProfileModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setEditingParticipant(null);
            setEditingSceneProfile(null);
            setIsEditModalOpen(false);
          }}
          onSave={handleSaveParticipantEdit}
          initialProfile={editingParticipant?.profile?.description}
          token={token}
        />
      )}
      {editingParticipant && editingSceneProfile === "scene" && (
        <EditSceneModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditingSceneProfile(null);
          }}
          onSave={handleSaveParticipantEdit}
          initialScene={(editingParticipant as types.CharacterInChatRoom).scene?.description}
        />
      )}
      <ConfirmDialog
        isOpen={isDeleteLastMessageDialogOpen}
        onClose={() => setIsDeleteLastMessageDialogOpen(false)}
        onConfirm={async () => {
          await handleDeleteLastMessage();
          setIsDeleteLastMessageDialogOpen(false);
        }}
        title="直近のメッセージを削除"
        description="直近のメッセージを削除してもよろしいですか？"
      />
      <ConfirmDialog
        isOpen={isDeleteAllMessagesDialogOpen}
        onClose={() => setIsDeleteAllMessagesDialogOpen(false)}
        onConfirm={async () => {
          await handleDeleteAllMessages();
          setIsDeleteAllMessagesDialogOpen(false);
        }}
        title="警告: すべてのメッセージを削除"
        description="この操作はすべてのメッセージを完全に削除します。この操作は取り消せません。本当に続行しますか？"
        isDangerous={true}
      />
      <ImageUploadModal
        isOpen={isImageUploadModalOpen}
        onClose={() => {
          setIsImageUploadModalOpen(false);
          setCurrentUploadingParticipant(null);
        }}
        onUploadNew={handleUploadNewImage}
        onSelectExisting={handleSelectExistingImage}
        existingImages={existingImages}
        isUploading={isUploadingImage}
      />
    </div>
  );
};
